// 2026-07-25:GA4 API client(/api/ga4/*)。
//
// 后端 endpoints:
// - GET    /api/ga4/connection        → Ga4Connection | 404
// - DELETE /api/ga4/connection        → {status:"deleted"} | 404
// - GET    /api/ga4/auth/url          → {url, state}
// - POST   /api/ga4/auth/select-property  → Ga4Connection
// - GET    /api/ga4/report?period=... → adapted JSON for AnalyticsPage + Dashboard
//
// 不返回任何 refresh_token / Fernet encrypted 字段。

import { apiFetch } from "./client";

export type Ga4Period = "today" | "yesterday" | "7d" | "30d" | "thismonth" | "prevmonth";

export interface SyncMeta {
  stale: boolean;
  sync_status: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  ttl_seconds: number;
}

export interface Ga4Connection {
  google_email: string;
  property_id: string;
  property_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface Ga4PropertySummary {
  property_id: string;
  property_name: string;
  account: string;
}

export interface Ga4AuthUrlResponse {
  url: string;
  state: string;
}

export interface Ga4OAuthCallbackResponse {
  google_email: string;
  properties: Ga4PropertySummary[];
  pending_token: string;
}

export interface Ga4Kpis {
  totalUsers: number;
  screenPageViews: number;
  averageSessionDuration: number; // seconds (float)
  bounceRate: number;              // ratio 0-1
  conversions: number;
  change: {
    totalUsers: number | null;
    screenPageViews: number | null;
    averageSessionDuration: number | null;
    bounceRate: number | null;
    conversions: number | null;
  };
}

export interface Ga4DateRange {
  startDate: string;
  endDate: string;
}

export interface Ga4Comparison {
  label: string;
  current: Ga4DateRange;
  previous: Ga4DateRange;
}

export interface Ga4MetricTrends {
  totalUsers: number[];
  screenPageViews: number[];
  averageSessionDuration: number[];
  bounceRate: number[];
  conversions: number[];
}

export interface Ga4TrendPoint {
  // 后端返 int(sessions)。前端だけ date を保持することも可能だが今回は trend は sessions 配列のみ
  sessions: number;
}

export interface Ga4SourceEntry {
  label: string;
  value: number;
  color: string;
}

export interface Ga4AudienceEntry {
  label: string;
  value: number;
  color: string;
}

export interface Ga4Audience {
  newUsersTrend: number[];
  countries: Ga4AudienceEntry[];
  cities: Ga4AudienceEntry[];
  devices: Ga4AudienceEntry[];
}

export interface Ga4PageEntry {
  title?: string;
  hostName?: string;
  path: string;
  pv: number;
  uu: number;
  avgTime: number; // seconds
  bounce: number;  // ratio 0-1
}

export interface Ga4Report {
  kpis: Ga4Kpis;
  trend: number[];            // sessions by day (oldest → newest)
  metricTrends?: Ga4MetricTrends;
  sources: Ga4SourceEntry[];
  pages: Ga4PageEntry[];
  audience?: Ga4Audience;
  period: Ga4Period;
  comparison?: Ga4Comparison | null;
  sync_meta?: SyncMeta;
}

export interface Ga4AiInsightResponse {
  text: string;
  status: string;
  usage?: Record<string, unknown>;
  insight?: AiInsightData;
}

export interface AiInsightItem {
  severity: "positive" | "warning" | "critical" | "neutral" | string;
  icon: string;
  title: string;
  description: string;
}

export interface AiInsightAction {
  action_id: string;
  priority: "high" | "medium" | "low" | string;
}

export interface AiInsightData {
  summary?: string;
  insights?: AiInsightItem[];
  actions?: AiInsightAction[];
  questions?: string[];
}

export const ga4Api = {
  // OAuth が backend env に設定済みか。
  // false の 場合 frontend は 接続ボタン disabled + 案内文 表示。
  // 認証不要で呼べる(/api/ga4/status 自身は guard なし)。
  getStatus: () =>
    fetch("/api/ga4/status", { credentials: "include" }).then(async (res) => {
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) {
        return { configured: false };
      }
      return j.data as { configured: boolean };
    }),

  getConnection: () => apiFetch<Ga4Connection>("/ga4/connection"),

  deleteConnection: () =>
    apiFetch<{ status: "deleted" }>("/ga4/connection", { method: "DELETE" }),

  getAuthUrl: () => apiFetch<Ga4AuthUrlResponse>("/ga4/auth/url"),

  selectProperty: (body: { pending_token: string; property_id: string; property_name: string | null }) =>
    apiFetch<Ga4Connection>("/ga4/auth/select-property", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getReport: (period: Ga4Period = "30d") =>
    apiFetch<Ga4Report>(`/ga4/report?period=${encodeURIComponent(period)}`),

  refreshReport: (period: Ga4Period, force = true) =>
    apiFetch<{ refreshed: string[]; skipped: string[]; ga4_period: string }>("/ga4/report/refresh", {
      method: "POST",
      body: JSON.stringify({ period, force }),
    }),

  generateAiInsight: (body: { period: Ga4Period; question?: string }) =>
    apiFetch<Ga4AiInsightResponse>("/ga4/ai-insight", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
